import { checkDeploymentStatus } from '~/lib/api/netlify';
import type { DeploymentStatus, DeploymentStatusResponse, OAuthResponse, ChatDeployment } from '~/lib/types/netlify';
import * as Sentry from '@sentry/remix';
import { getMessagesById } from '~/lib/persistence/db';
import { createNetlifySite } from '~/lib/api/netlify';
import { description, db, chatId } from '~/lib/persistence/useChatHistory';
import { generateRandomString } from '~/lib/utils';
import { updateNetlifyUrl } from '~/lib/persistence/db';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { userStore } from '~/lib/stores/user';

export const handleOAuthCallback = async (
  code: string,
  navigate: (path: string, options?: { replace?: boolean }) => void,
  pathname: string,
  setAccessToken: (token: string) => void,
) => {
  const response = await fetch('/api/netlify-oauth/callback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });

  const data = (await response.json()) as OAuthResponse;

  if (data.access_token) {
    setAccessToken(data.access_token);
    selectionStore.setKey('netlifyAccessToken', data.access_token);
    localStorage.removeItem('netlifyState');
    navigate(pathname, { replace: true });
  }
};

export const checkAndUpdateDeploymentStatus = async (
  accessToken: string,
  deployedId: string,
  setDeploymentStatus: (status: DeploymentStatus) => void,
  setDeployedUrl: (url: string) => void,
  setAdminUrl: (url: string) => void,
  setIsCheckingStatus: (status: boolean) => void,
  setError: (error: string) => void,
) => {
  try {
    console.log('[netlify-helpers] Fetching deployment status for deployId:', deployedId);

    const status = await checkDeploymentStatus(accessToken, deployedId);
    const statusResponse = status as DeploymentStatusResponse;

    console.log('[netlify-helpers] Deployment status response:', statusResponse);

    console.log('Deployment status response:', statusResponse);

    if (statusResponse.admin_url) {
      const adminUrl = `${statusResponse.admin_url}/deploys/${deployedId}`;
      console.log('[netlify-helpers] Setting adminUrl to:', adminUrl);
      setAdminUrl(adminUrl);
    } else {
      console.log('[netlify-helpers] No admin_url in status response');
    }

    switch (statusResponse.state) {
      case 'new':
      case 'building': {
        console.log('[netlify-helpers] Setting deployment status to:', statusResponse.state);
        setDeploymentStatus(statusResponse.state as DeploymentStatus);
        setIsCheckingStatus(true);
        break;
      }
      case 'ready': {
        console.log('[netlify-helpers] Deployment ready, handling successful deploy');
        handleSuccessfulDeploy(statusResponse, setDeploymentStatus, setDeployedUrl, setIsCheckingStatus);
        break;
      }
      case 'error': {
        console.log('[netlify-helpers] Deployment error, handling deploy error');
        handleDeployError(setDeploymentStatus, setError, setIsCheckingStatus);
        break;
      }
    }
  } catch (err) {
    Sentry.captureException(err, {
      extra: { accessToken, deployedId, message: 'Error in netlify deployment' },
      level: 'log',
    });

    handleDeployError(setDeploymentStatus, setError, setIsCheckingStatus);
  }
};

export const checkExistingDeployment = async (db: any, chatId: any) => {
  if (!db || !chatId) {
    return { url: null, siteId: null, inspectDeployment: null };
  }

  try {
    const chat = await getMessagesById(db, chatId);
    const chatData = 'netlifyCreds' in chat ? (chat as ChatDeployment) : null;

    return {
      url: chatData?.netlifyCreds?.netlifyUrl || null,
      siteId: chatData?.netlifyCreds?.netlifySiteId || null,
      inspectDeployment: chatData?.netlifyCreds?.inspectDeployment || null,
    };
  } catch (error) {
    Sentry.captureException(error, {
      extra: { chatId, message: 'Error checking previous deployment' },
      level: 'log',
    });
    console.error('Error checking previous deployment:', error);
    return { url: null, siteId: null };
  }
};

export const ensureSiteExists = async (accessToken: string): Promise<string | null> => {
  let siteId: string | null = null;

  // Get base project name from description or default
  const baseProjectName = (description.value ?? 'project')
    .toLowerCase()
    .split(' ')
    .join('_')
    .replace(/[^a-z0-9_-]/g, ''); // Remove any invalid characters

  // Generate timestamp and suffix
  const timestampHash = Date.now().toString(36).slice(-4);
  const suffix = '_alphaproject';

  // Calculate max length for project name
  const maxLength = 30 - (timestampHash.length + suffix.length + 2);

  // Truncate project name if needed
  const truncatedName = baseProjectName.slice(-maxLength);

  // Combine parts to create unique name
  const uniqueProjectName = `${truncatedName}_${timestampHash}${suffix}`;

  try {
    const site = await createNetlifySite(accessToken, uniqueProjectName);
    siteId = site.id;
    selectionStore.setKey('netlifySiteId', siteId);
  } catch (err) {
    Sentry.captureException(err, {
      extra: { accessToken, siteId, message: 'Error creating Netlify site' },
    });

    if (err instanceof Error && err.message.includes('subdomain')) {
      throw new Error('Failed to create site: Site name already taken');
    }

    throw err;
  }
  return siteId;
};

const handleSuccessfulDeploy = async (
  statusResponse: DeploymentStatusResponse,
  setDeploymentStatus: (status: DeploymentStatus) => void,
  setDeployedUrl: (url: string) => void,
  setIsCheckingStatus: (status: boolean) => void,
) => {
  setTimeout(() => {
    setDeploymentStatus('ready' as DeploymentStatus);
  }, 1000);

  const deployUrl = statusResponse.ssl_url || statusResponse.url || '';
  console.log('[netlify-helpers] Deployment successful, URL:', deployUrl);
  setDeployedUrl(deployUrl);
  setIsCheckingStatus(false);

  // Update Netlify URL in database
  if (db && chatId.get()) {
    try {
      const siteId = selectionStore.get().netlifySiteId;
      const token = selectionStore.get().netlifyAccessToken;

      if (!deployUrl || !siteId || !token) {
        console.warn('[netlify-helpers] Missing data for DB update:', {
          hasUrl: !!deployUrl,
          hasSiteId: !!siteId,
          hasToken: !!token,
        });
        return;
      }

      console.log('[netlify-helpers] Updating Netlify URL in database:', deployUrl);
      await updateNetlifyUrl(db, chatId.get()!, {
        netlifyUrl: deployUrl,
        netlifySiteId: siteId || '',
        netlifyToken: token || '',
        inspectDeployment: statusResponse.admin_url || '',
      });

      console.log('[netlify-helpers] Successfully updated Netlify data in database');
    } catch (error) {
      Sentry.captureException(error, {
        extra: { chatId: chatId.get(), message: 'Error saving Netlify data' },
      });

      console.error('Failed to save Netlify data:', error);
    }
  }
};

const handleDeployError = (
  setDeploymentStatus: (status: DeploymentStatus) => void,
  setError: (error: string) => void,
  setIsCheckingStatus: (status: boolean) => void,
) => {
  console.error('[netlify-helpers] Handling deployment error');

  setDeploymentStatus('error' as DeploymentStatus);
  setError('Build failed. View deployment logs to fix issues.');
  setIsCheckingStatus(false);
};

export const initiateOAuth = (
  netlifyConfig: {
    NEXT_PUBLIC_NETLIFY_CLIENT_ID: string;
    NEXT_PUBLIC_NETLIFY_APP_URL: string;
  },
  setIsCheckingToken: (value: boolean) => void,
  setTokenCheckCount: (value: number) => void,
) => {
  const state = generateRandomString(16);
  localStorage.setItem('netlifyState', state);
  setIsCheckingToken(true);
  setTokenCheckCount(0);

  const authUrl = new URL('https://app.netlify.com/authorize');
  authUrl.searchParams.append('client_id', netlifyConfig.NEXT_PUBLIC_NETLIFY_CLIENT_ID);
  authUrl.searchParams.append('response_type', 'code');
  authUrl.searchParams.append('state', state);
  authUrl.searchParams.append('redirect_uri', netlifyConfig.NEXT_PUBLIC_NETLIFY_APP_URL);

  window.open(authUrl.toString(), '_blank');
};

// Add utility function for copying to clipboard
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy:', error);
    return false;
  }
};

// Reset OAuth state without starting the flow
export const resetOAuthState = (
  setIsCheckingToken: (value: boolean) => void,
  setTokenCheckCount: (value: number) => void,
) => {
  setIsCheckingToken(false);
  setTokenCheckCount(0);
};
